import { Button, Form, Input, Popconfirm } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { userApi } from '../../../../../apis'
import { AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { RootState } from '../../../../../stores'
import {
  setActionProcessing,
  setAlert,
  setShowManageModal,
  setShowResetPasswordModal,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

export const ResetPasswordModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { language } = useSelector((state: RootState) => state.language)
  const { selectedUser } = useSelector((state: RootState) => state.user)
  const { management } = useSelector((state: RootState) => state.common)

  const onFinish = async () => {
    try {
      dispatch(setActionProcessing(true))
      dispatch(setAlert())
      await userApi.resetPassword(
        selectedUser?.id || 0,
        form.getFieldValue('newPassword')
      )
      dispatch(
        setAlert({
          key: 'user.resetPassword.success',
          type: AlertMessageEnum.SUCCESS,
        })
      )
    } catch (e) {
      if (e.response && e.response.status && e.response.status === 401) {
        await userApi.resetPassword(
          selectedUser?.id || 0,
          form.getFieldValue('newPassword')
        )
        dispatch(
          setAlert({
            key: 'user.resetPassword.success',
            type: AlertMessageEnum.SUCCESS,
          })
        )
      } else {
        dispatch(
          setAlert({
            key: 'user.resetPassword.fail',
            type: AlertMessageEnum.ERROR,
          })
        )
      }
    } finally {
      dispatch(setActionProcessing(false))
    }
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        (management.alertMessage &&
          management.alertMessage.type !== AlertMessageEnum.SUCCESS)) && (
        <>
          <StyledFormItem
            label={
              <StyledLabel>
                {translate('user.resetPassword.description', language)}
              </StyledLabel>
            }
          >
            {`${selectedUser?.username} (${selectedUser?.name})`}
          </StyledFormItem>
          <StyledFormItem
            name="newPassword"
            label={
              <StyledLabel>
                {translate('user.newPassword', language)}
              </StyledLabel>
            }
            rules={[
              {
                required: true,
                message: translate('user.invalid.password', language),
                pattern: new RegExp(
                  /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+){8,}$/
                ),
              },
            ]}
          >
            <Input.Password
              placeholder={translate('user.newPassword', language)}
              allowClear
            />
          </StyledFormItem>
          <StyledFormItem
            name="confirmPassword"
            label={
              <span style={{ fontWeight: 'bold' }}>
                {translate('user.confirmPassword', language)}
              </span>
            }
            rules={[
              {
                required: true,
                message: '',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(
                      new Error(translate('user.invalid.password', language))
                    )
                  } else if (value !== getFieldValue('newPassword')) {
                    return Promise.reject(
                      new Error(
                        translate('user.invalid.confirmPassword', language)
                      )
                    )
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input.Password
              allowClear
              placeholder={translate('user.confirmPassword', language)}
            />
          </StyledFormItem>
        </>
      )}
      <ManageModalFooterComponent
        onClose={() => {
          dispatch(setAlert())
          dispatch(setShowResetPasswordModal(false))
          dispatch(setShowManageModal(true))
        }}
        customButtons={
          <>
            {(!management.alertMessage ||
              (management.alertMessage &&
                management.alertMessage.type !== AlertMessageEnum.SUCCESS)) && (
              <Popconfirm
                title={translate('user.resetPassword.confirm', language)}
                okText={translate('common.yes', language)}
                cancelText={translate('common.no', language)}
                placement="topRight"
                onConfirm={() => form.submit()}
              >
                <Button type="primary" loading={management.actionProcessing}>
                  {translate(
                    `user.${
                      management.actionProcessing ? 'reseting' : 'reset'
                    }`,
                    language
                  )}
                </Button>
              </Popconfirm>
            )}
          </>
        }
      />
    </Form>
  )

  return (
    <ManageModalComponent
      body={body}
      title={translate('user.resetPassword.title', language)}
    />
  )
}
